import React from "react";
import { UseQueryOptions } from "react-query";

import { AnyState } from "../../../services/query";
import activities from "../../../redux/activities";
import { OperationsDataEnvelope } from "../../../models/operations";

import { usePreOperationsQuery } from "./preOperations/preOperations.query";
import { useOperationQuery } from "./operation/operation.query";
import { useMaybeCurrentOperationId } from "./currentOperationId";

import {
  computeCombinedData,
  computeCombinedError,
  computeCombinedStatus,
  computeEnvelope,
} from "./operations.query.helpers";
import { useSetOperationsGlobalActivityAndStatus } from "./operations.globalStatus";

// ########################
// ## useOperationsQuery ##
// ########################

export type ParamsOfUseOperationsQuery = {
  watch?: UseQueryOptions["notifyOnChangeProps"];
};
export type ResultOfUseOperationsQuery = AnyState<OperationsDataEnvelope>;
export type UseOperationsQuery = (
  params?: ParamsOfUseOperationsQuery,
) => ResultOfUseOperationsQuery;

const defaultWatch: UseQueryOptions["notifyOnChangeProps"] = [
  "data",
  "error",
  "status",
];

/**
 * The returned value of this hook will contain the React Query result
 * of the fetching of the operations data.
 *
 * This hook is only meant for use by the hooks in `./operations.ts`.
 * Do not use this hook outside these hooks as this could couple us to React Query.
 */
export const useOperationsQuery: UseOperationsQuery = ({
  watch = defaultWatch,
} = {}) => {
  const currentOperationId = useMaybeCurrentOperationId();
  const currentOperation = useOperationQuery({
    watch,
    operationId: currentOperationId,
  });
  const operations = usePreOperationsQuery({
    watch,
  });

  const setOperationsGlobalActivityAndStatus =
    useSetOperationsGlobalActivityAndStatus();

  const combinedData = computeCombinedData(
    currentOperation.data,
    operations.data,
  );

  const combinedStatus = computeCombinedStatus(
    currentOperation.status,
    operations.status,
  );

  const combinedError = computeCombinedError(
    currentOperation.error,
    operations.error,
  );

  // Why a useEffect? So that this only sets the global activity
  // and status when combinedStatus changes, as opposed to on
  // every render. This is a pain but is only necessary to do
  // while we preserve the behavior the deprecated data hooks.
  React.useEffect(() => {
    setOperationsGlobalActivityAndStatus({
      status: combinedStatus,
      activity: activities.getListOfOperations,
    });
  }, [setOperationsGlobalActivityAndStatus, combinedStatus]);

  return computeEnvelope(combinedData, combinedStatus, combinedError);
};
